/* ------------- Per-processor building-blocks helpers ------------- */

import { createSelector } from "@reduxjs/toolkit"

// A leaf module: it imports nothing from the slice or the thunks, so both can read it
// without a circular dependency (the thunks need the same active-processor view the
// selectors expose).

// Steps 2 onward. these sections are filled per connected processor; Integrate
// (processors/tools) is global and stays at the top level of buildingBlocks.
// Enrichment keeps a per-processor bucket for the view's shape only: its tools are
// connected per business account, and selectBuildingBlocks fills every bucket from the
// backend's catalog (see there).
export const SCHEMA_SECTIONS = [
  "enrichment",
  "required_information",
  "general_information",
  "checkout_policies",
  "verifications",
  "evidence_vault"
]

// Normalize an incoming building_blocks payload into the per-processor shape:
// { processors, tools, byProcessor: { <slug>: { <section>: {...} } } }. A legacy
// flat payload (sections at the top level, the old single-profile shape) is
// migrated by seeding every connected processor's bucket with those sections, so
// nothing is lost and a single-processor merchant is unchanged.
export const normalizeBuildingBlocks = (bb) => {
  const src = bb || {}
  const processors = src.processors || { connected: [] }
  const tools = src.tools || { connected: [] }
  const connected = Array.isArray(processors.connected) ? processors.connected : []
  const flatSections = {}
  SCHEMA_SECTIONS.forEach((s) => {
    if (src[s] !== undefined) flatSections[s] = src[s]
  })
  const byProcessor = { ...(src.byProcessor || {}) }
  connected.forEach((proc) => {
    if (!byProcessor[proc]) byProcessor[proc] = { ...flatSections }
  })
  return { processors, tools, byProcessor }
}

// The processor whose steps are currently shown: the explicit activeProcessor if
// it is still connected, else the first connected one (fallback), else null.
export const activeProcessorOf = (profile) => {
  const connected = profile?.buildingBlocks?.processors?.connected || []
  const active = profile?.activeProcessor
  if (active && connected.includes(active)) return active
  return connected[0] || null
}

// Read one section's current data for the active processor (falls back to a
// legacy top-level section if the shape hasn't been normalized yet).
export const readSection = (profile, section) => {
  const bb = profile?.buildingBlocks || {}
  const active = activeProcessorOf(profile)
  return (active ? bb.byProcessor?.[active]?.[section] : bb[section]) ?? {}
}

// Return a new buildingBlocks with one section replaced, scoped to the active
// processor's bucket.
export const writeSection = (profile, section, sectionData) => {
  const bb = profile?.buildingBlocks || {}
  const active = activeProcessorOf(profile)
  if (!active) return { ...bb, [section]: sectionData }
  const byProcessor = { ...(bb.byProcessor || {}) }
  byProcessor[active] = { ...(byProcessor[active] || {}), [section]: sectionData }
  return { ...bb, byProcessor }
}

// No answer at all: nothing typed, nothing picked. An object (a phone, an upload) counts as
// blank only when every part of it is.
const isBlank = (value) => {
  if (value === null || value === undefined) return true
  if (typeof value === "string") return value.trim() === ""
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object") return Object.values(value).every(isBlank)
  return false
}

// Return a new buildingBlocks where every question still blank here takes the value the
// backend now holds. This is how the prefill readings, which land server-side after the
// form has loaded, reach a merchant already on it — without touching anything they have
// typed, and without marking a section dirty for values that are already saved.
export const fillBlankAnswers = (local, fetched) => {
  const byProcessor = { ...(local?.byProcessor || {}) }

  Object.entries(fetched?.byProcessor || {}).forEach(([proc, sections]) => {
    const bucket = { ...(byProcessor[proc] || {}) }

    SCHEMA_SECTIONS.forEach((section) => {
      const incoming = sections?.[section]
      if (!incoming) return
      const current = bucket[section] || {}
      const blanks = Object.entries(incoming).filter(
        ([key, value]) => isBlank(current[key]) && !isBlank(value)
      )
      if (blanks.length) bucket[section] = { ...current, ...Object.fromEntries(blanks) }
    })

    byProcessor[proc] = bucket
  })

  return { ...(local || {}), byProcessor }
}

/* ------------- Selectors ------------- */
// Re-exported from configure.slice.js so components keep importing selectors from the
// slice; the thunks import them from here to stay off the slice's import path.

export const selectProfile = (state) => state.disputesConfigure.profile
export const selectIsActivated = (state) => state.disputesConfigure.profile.isActivated
export const selectEverActivated = (state) => state.disputesConfigure.profile.everActivated

// Shared empties so a missing slice value keeps a stable identity: useSelector compares
// with ===, and a fresh [] / {} per call re-renders every consumer on every action.
const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

// Which processors activation covers, and which ones have finished setup. Both come
// from the account: nothing about setup or coverage is remembered in this browser.
export const selectActivatedProcessors = (state) =>
  state.disputesConfigure.profile.activatedProcessors || EMPTY_ARRAY

export const selectSetupState = (state) => state.disputesConfigure.profile.setup || EMPTY_OBJECT

export const selectFinishedProcessors = createSelector([selectSetupState], (setup) =>
  Object.entries(setup)
    .filter(([, entry]) => entry?.setup_finished)
    .map(([slug]) => slug)
)

const selectStoredBuildingBlocks = (state) => state.disputesConfigure.profile.buildingBlocks

export const selectEnrichmentCatalog = (state) =>
  state.disputesConfigure.profile.enrichmentCatalog || EMPTY_OBJECT

export const selectBuildingBlocks = createSelector(
  [selectStoredBuildingBlocks, selectEnrichmentCatalog],
  (buildingBlocks, catalog) => {
    if (!buildingBlocks?.byProcessor) return buildingBlocks

    const connected = (catalog.tools || EMPTY_ARRAY)
      .filter((tool) => tool.connected)
      .map((tool) => tool.id)
    const byProcessor = {}

    Object.entries(buildingBlocks.byProcessor).forEach(([slug, sections]) => {
      byProcessor[slug] = {
        ...sections,
        enrichment: { ...(sections?.enrichment || {}), connected }
      }
    })

    return { ...buildingBlocks, byProcessor }
  }
)

// The processors the merchant has connected. Read by the rail, the footer, the summary
// and the section form, so it is worth one definition rather than four inline reads.
export const selectConnectedProcessors = (state) =>
  state.disputesConfigure.profile.buildingBlocks?.processors?.connected || EMPTY_ARRAY

// The active processor slug (resolved with the connected-list fallback).
export const selectActiveProcessor = (state) => activeProcessorOf(state.disputesConfigure.profile)

// The flat building-blocks view for the active processor: its five sections
// hoisted to the top level (so every existing consumer keeps reading
// buildingBlocks.required_information etc.), plus the global processors/tools.
export const selectActiveBuildingBlocks = createSelector(
  [selectBuildingBlocks, selectActiveProcessor],
  (buildingBlocks, active) => {
    const bb = buildingBlocks || EMPTY_OBJECT
    const perProc = (active && bb.byProcessor?.[active]) || EMPTY_OBJECT
    return { ...perProc, processors: bb.processors, tools: bb.tools, byProcessor: bb.byProcessor }
  }
)

export const selectUi = (state) => state.disputesConfigure.ui

// Whether the prefill collector is still reading any connected processor.
export const selectPrefillCollecting = (state) =>
  Object.values(state.disputesConfigure.profile.prefill || EMPTY_OBJECT).some(
    (entry) => entry?.collecting
  )
